import {CloudNode} from "./cloud-node";
import {readFileBuffer} from "../file-system-loader";
import { EncompassPointCloud } from '.';
import {
  Points,
  Vector3
} from 'three';

export class EncompassPointCloudNode extends CloudNode {
  protected cloud: EncompassPointCloud;
  public parent: EncompassPointCloudNode;
  private level: any;
  private _children = null;

  constructor(cloud: EncompassPointCloud, parent, name, level, center, size) {
    super();

    this.cloud = cloud;
    this.parent = parent;
    this.name = name;
    this.level = level;
    this.size = size;
    this._center = center;
  }

  /** Get children nodes if they exist, or assume all 8 children exist */
  get children() {
    if (this._children === null) {
      this.clearChildNodes();

      for (var i = 0; i < 8; i++) {
        let child = this.getChildAtIndex(i);
        this.addChildNode(child);
      }
    }

    return this._children;
  }

  get shift() {
    return this.cloud.shift;
  }

  get precision() {
    return this.cloud.precision;
  }

  addToScene() {
    // Create pointcloud object
    let pointsScene = new Points(this.geometry,
      this.cloud.materialScene);
    let pointsPicker = new Points(this.geometry,
      this.cloud.materialPicker);

    pointsScene.visible = false;
    pointsPicker.visible = false;

    // Create bounding box object;
    let boundingBox = this.cloud.generateEdgeMesh(
      this.size, this.size, this.size);

    this.addCloudData(pointsPicker, pointsScene, boundingBox);
  }

  getGeometry(data) {
    this.setGeometry(data);

    this.numPoints = data.numPoints;
    this.tightBoundingBox = {
      min: data.xyz_min,
      max: data.xyz_max
    };

    // Update unique values and min/max values
    this.cloud.updateClassifications(data.classification);
    this.cloud.updateColorLimits(data.rgb_max);
    this.cloud.updateIntensityRange(data.intensity_min, data.intensity_max);
    this.cloud.updateBoundingBox(this.tightBoundingBox);

    this.loaded = true;
    this.loading = false;

    // console.log(`Loaded geometry [${this.name}]`);
  }

  getBinaryData() {
    let dataPath = this.cloud.getDataPath(this.name);

    let onSuccess = (data: Buffer) => {
      this.sendToWorker({
        buffer: data,
        precision: this.precision,
        shift: this.shift
      });
    };

    let onError = () => {
      if (this.level === 1) {
        return;
      }

      this.parent.removeChildNode(this);
    };

    let path = `${dataPath}/${this.name}`;

    // Read node data
    readFileBuffer(path, this.cloud.isRemoteFile)
      .then(onSuccess)
      .catch(onError);
  }

  sendToWorker(data) {
    let workerPool = this.cloud.workerPool;

    let worker = workerPool.getWorker();

    worker.onmessage = event => {
      const data = event.data;

      // Convert array to vector
      data.xyz_min = new Vector3(...data.xyz_min);
      data.xyz_max = new Vector3(...data.xyz_max);

      // Convert raw buffer to float array
      data.positions = new Float32Array(data.positions);
      data.intensity = new Float32Array(data.intensity);
      data.classification = new Float32Array(data.classification);
      data.colors = new Float32Array(data.colors);

      this.getGeometry(data);
      workerPool.returnWorker(worker);
    };

    workerPool.sendMessage(worker, data, [data.buffer]);
  }

  getChildNameAtIndex(index) {
    return `${this.name}${index}`;
  }

  getChildAtIndex(index) {
    let childName = this.getChildNameAtIndex(index);
    let childSize = this.size / 2.0;
    let childLevel = this.level + 1;

    let z = Math.floor(index / 4);
    let y = Math.floor((index - (4 * z)) / 2);
    let x = Math.floor(index % 2);

    let childCenter = new Vector3(
      this._center.x + (x - 0.5) * childSize,
      this._center.y + (y - 0.5) * childSize,
      this._center.z + (z - 0.5) * childSize
    );

    return new EncompassPointCloudNode(this.cloud, this, childName,
      childLevel, childCenter, childSize);
  }

  addChildNode(node) {
    this._children.push(node);
  }

  clearChildNodes() {
    if (this._children) {
      this._children.forEach(child => {
        child = null;
      });
    }
    this._children = [];
  }

  removeChildNode(node) {
    let index = this._children.indexOf(node);
    if (index !== -1) {
      this._children.splice(index, 1);
    }

    node.clearChildNodes();
    node = null;
  }

  load() {
    if ((this.loaded) || (this.loading)) {
      return;
    }

    this.loading = true;
    this.getBinaryData();
  }
}
